import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type HealthResponse, type MediaReadiness } from "../../api/client";

/** One probe's outcome. `unknown` means it has not been answered yet. */
export type ProbeState = "unknown" | "ok" | "failing";

export interface HealthSnapshot {
  api: ProbeState;
  control: ProbeState;
  media: ProbeState;
  version?: string;
  mediaPlane?: string;
  adapter?: string;
  instanceId?: string;
  lastCheckedAt?: Date;
  lastError?: string;
}

/**
 * Polls the three probes.
 *
 * Web readiness and media readiness are kept apart on purpose: a media node
 * that cannot place calls must stop admitting them without taking the API
 * offline (docs/plans/02-architecture.md §4).
 */
export function useHealth(intervalMs = 10_000): {
  snapshot: HealthSnapshot;
  refresh: () => void;
} {
  const [snapshot, setSnapshot] = useState<HealthSnapshot>({
    api: "unknown",
    control: "unknown",
    media: "unknown",
  });

  const refresh = useCallback(() => {
    let cancelled = false;

    const run = async () => {
      const next: HealthSnapshot = {
        api: "unknown",
        control: "unknown",
        media: "unknown",
        lastCheckedAt: new Date(),
      };

      try {
        const health: HealthResponse = await api.health();
        next.api = "ok";
        next.version = health.version;
      } catch (error) {
        next.api = "failing";
        next.lastError = describeError(error);
      }

      try {
        await api.ready();
        next.control = "ok";
      } catch (error) {
        next.control = "failing";
        next.lastError ??= describeError(error);
      }

      try {
        const media: MediaReadiness = await api.mediaReady();
        next.media = "ok";
        next.mediaPlane = media.media_plane;
        next.adapter = media.adapter;
        next.instanceId = media.instance_id;
      } catch (error) {
        next.media = "failing";
        next.lastError ??= describeError(error);
      }

      if (!cancelled) {
        setSnapshot(next);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const stop = refresh();
    const timer = window.setInterval(refresh, intervalMs);
    return () => {
      stop();
      window.clearInterval(timer);
    };
  }, [refresh, intervalMs]);

  return { snapshot, refresh };
}

function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    return `${error.body.code}: ${error.message}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "unreachable";
}
